import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';

/**
 * PermissionGuard — for slug-based routes inside DatabaseProvider context.
 * Checks the permission key against the current DB slug.
 */
export const PermissionGuard = ({ permKey, children }) => {
  const { isSysAdmin, hasPermission } = useAuth();
  const dbContext = useDatabaseSafe();
  const dbSlug = dbContext?.dbSlug || '';
  if (isSysAdmin || hasPermission(permKey, dbSlug)) return children;
  return <Navigate to="/access-denied" replace />;
};

/**
 * GlobalPermissionGuard — for slug-free routes (dev tools, etc.).
 * Checks if the permission key exists in the user's global permissions.
 */
export const GlobalPermissionGuard = ({ permKey, children }) => {
  const { isSysAdmin, hasPermission } = useAuth();
  if (isSysAdmin || hasPermission(permKey, 'global')) return children;
  return <Navigate to="/access-denied" replace />;
};

/**
 * SysAdminGuard — hardcoded SysAdmin check for /admin/* routes.
 * Never permission-key driven — ensures we can never lock ourselves out.
 */
export const SysAdminGuard = ({ children }) => {
  const { isSysAdmin } = useAuth();
  if (isSysAdmin) return children;
  return <Navigate to="/access-denied" replace />;
};

/**
 * AnyReportsPermGuard — passes if the user has ANY reports.* perm.
 * Post 2026-08-05 the section-level module.reports key was retired; a
 * user with any single reports.<section> should reach the Reports
 * landing + generic viewer, and per-report backend enforcement handles
 * the finer gate. SysAdmin bypasses.
 */
export const AnyReportsPermGuard = ({ children }) => {
  const { isSysAdmin, hasPermission } = useAuth();
  const dbContext = useDatabaseSafe();
  const dbSlug = dbContext?.dbSlug || '';
  const SECTIONS = ['general', 'submittals', 'networking', 'quality', 'commissioning', 'design', 'dynamic'];
  if (isSysAdmin) return children;
  if (SECTIONS.some(s => hasPermission(`reports.${s}`, dbSlug))) return children;
  return <Navigate to="/access-denied" replace />;
};

/**
 * AnyIntegrationPermGuard — passes if the user has ANY integration.* tool
 * perm. Post 2026-08-05 the section-level integration.integration key
 * was retired; section access is now implied by having any child perm.
 */
export const AnyIntegrationPermGuard = ({ children }) => {
  const { isSysAdmin, hasPermission } = useAuth();
  const dbContext = useDatabaseSafe();
  const dbSlug = dbContext?.dbSlug || '';
  const TOOLS = ['udtcomment', 'scadatagen', 'simtagen', 'iobuffer', 'plcplatforms', 'tagxref'];
  if (isSysAdmin) return children;
  if (TOOLS.some(t => hasPermission(`integration.${t}`, dbSlug))) return children;
  return <Navigate to="/access-denied" replace />;
};
